import { diff } from 'cpool-utils';

import objectKeys from '~/utils/objectKeys';

export interface LeafObject extends Record<string, string | number | boolean | Date | null> {
    id: string;
}
export type Objects<TKey extends string = string> = Record<TKey, Array<LeafObject>>;

function turnArrayIntoRecord<T extends LeafObject>(array: Array<T>) {
    const ids: Array<string> = [];
    const record = array.reduce((acc, curr) => {
        ids.push(curr.id);
        acc[curr.id] = curr;
        return acc;
    }, {} as Record<string, T>);
    return [ids, record] as const;
}

export function generateDiffIncome(oldData: Objects, newData: Objects) {
    const removed: Record<keyof typeof oldData, Array<LeafObject>> = {};
    const added: Record<keyof typeof oldData, Array<LeafObject>> = {};
    const modified: Record<keyof typeof oldData, Array<LeafObject>> = {};

    // first we create copies so that we dont mutate the original data by accident
    const previous = structuredClone(oldData);
    const current = structuredClone(newData);

    objectKeys(previous).forEach((key) => {
        // we turn the Arrays into a Record for faster access where the key is the id and the value is the object
        const [prevIds, prevRecord] = turnArrayIntoRecord(previous[key]);
        const [currIds, currRecord] = turnArrayIntoRecord(current[key]);

        // ids that are present in both prevIds and currIds - they might have been modified
        const keptIds: Array<string> = [];

        // iterate over the prevIds and check if they are included in the currIds, if not, we need to create a remove command
        prevIds.forEach((id) => {
            if (!currIds.includes(id)) {
                if (!removed[key]) removed[key] = [];
                removed[key].push(prevRecord[id]);
            } else {
                keptIds.push(id);
            }
        });

        // iterate over the currIds and check if they are not included in the prevIds, if so, we need to create a add command
        currIds.forEach((id) => {
            if (!prevIds.includes(id)) {
                if (!added[key]) added[key] = [];
                added[key].push(currRecord[id]);
            }
        });
        // compare the objects from the keptIds and see if they have been modified
        keptIds.forEach((id) => {
            const p = prevRecord[id];
            const c = currRecord[id];
            const difference = diff(p, c);
            if (typeof difference === 'object' && difference != null && Object.keys(difference).length > 0) {
                if (!modified[key]) modified[key] = [];
                modified[key].push(c);
            }
        });
    });

    return {
        added,
        modified,
        removed,
    };
}
